import { useDoModal } from '../../user/redux/hooks'
import { useAddUserAction } from '../../social/redux/hooks'
import { createInviteAction } from '../AlertSetMemberCard.helpers'

/**
 * Generic hook for handling group member save operations.
 * Consolidates the save logic used by both AlertSets and HeatStressGroupMembers.
 *
 * @param {Object} params
 * @param {Array} params.groupMembers - Current group members array
 * @param {Function} params.updateGroupMembers - Function to update group members
 * @param {string} params.userActionTypePrefix - Prefix for user action type ('alert-set-group-member' or 'heat-stress-group-member')
 * @param {boolean} params.showErrors - Whether to show error alerts (true for HeatStress, false for AlertSets)
 * @returns {Object} { handleSaveMember }
 */
export function useGroupMemberSave ({
  groupMembers,
  updateGroupMembers,
  userActionTypePrefix,
  showErrors = false
}) {
  const { doModal } = useDoModal()
  const { addUserAction } = useAddUserAction()

  const handleSaveMember = (result) => {
    const { memberData, inviteMethods, emailChanged, phoneChanged } = result

    // Determine if invites need to be sent
    const isNewMember = !memberData._id || memberData.isNew
    const shouldSendInvites = isNewMember && inviteMethods?.length > 0

    if (shouldSendInvites) {
      // Create invite actions for each method
      const invitePromises = inviteMethods.map(method => {
        const contactInfo = method === 'sms' ? memberData.phone : memberData.email
        const action = createInviteAction(
          userActionTypePrefix,
          method,
          memberData._id,
          contactInfo,
          memberData.name
        )

        // Return promise with method info for error handling
        return addUserAction(action)
          .then(res => ({ method, success: true, uaId: res._id }))
          .catch(err => ({ method, success: false, error: err }))
      })

      // Send all invites in parallel
      Promise.allSettled(invitePromises).then(results => {
        // Extract results
        const smsResult = results.find(r => r.value?.method === 'sms')
        const emailResult = results.find(r => r.value?.method === 'email')

        const smsAttempted = !!smsResult
        const emailAttempted = !!emailResult
        const smsSuccess = smsResult?.value?.success
        const emailSuccess = emailResult?.value?.success

        let errorMessage = null
        let memberToSave = { ...memberData, isNew: false }

        // Build member data based on successful invites
        if (smsSuccess) {
          memberToSave.smsUaId = smsResult.value.uaId
          memberToSave.phoneInvitePending = true
        }
        if (emailSuccess) {
          memberToSave.emailUaId = emailResult.value.uaId
          memberToSave.emailInvitePending = true
          memberToSave.emailVerified = false
        }

        // Determine error message based on what was attempted and what succeeded
        const failedMethods = []
        if (smsAttempted && !smsSuccess) {
          failedMethods.push('SMS')
          console.error('SMS error:', smsResult?.value?.error)
        }
        if (emailAttempted && !emailSuccess) {
          failedMethods.push('email')
          console.error('Email error:', emailResult?.value?.error)
        }

        if (failedMethods.length > 0) {
          const successfulMethods = []
          if (smsSuccess) successfulMethods.push('SMS')
          if (emailSuccess) successfulMethods.push('email')

          if (successfulMethods.length > 0) {
            // Partial success
            errorMessage = `${successfulMethods.join(' and ')} invite sent successfully, but ${failedMethods.join(' and ')} invite failed. You can resend the failed invite later.`
          } else {
            // All failed
            if (failedMethods.length === 1) {
              errorMessage = `Failed to send ${failedMethods[0]} invite. The member will be saved but you may need to send the invite later.`
            } else {
              errorMessage = 'Failed to send both invites. The member will be saved but you may need to send invites later.'
            }
          }
        }

        // Show error if any and showErrors is enabled
        if (errorMessage && showErrors) {
          alert(errorMessage)
        }

        // Always save the member
        const adminMember = groupMembers.find(m => m && m.admin)
        const otherMembers = groupMembers.filter(m => m && !m.admin)
        const newMembers = adminMember
          ? [adminMember, memberToSave, ...otherMembers]
          : [memberToSave]
        updateGroupMembers(newMembers)
        doModal(null)
      })
    } else if (emailChanged || phoneChanged) {
      // Prepare invite promises for changed contacts
      const invitePromises = []

      if (emailChanged && memberData.email) {
        const action = createInviteAction(
          userActionTypePrefix,
          'email',
          memberData._id,
          memberData.email,
          memberData.name
        )
        invitePromises.push(
          addUserAction(action)
            .then(res => ({ method: 'email', success: true, uaId: res._id }))
            .catch(err => ({ method: 'email', success: false, error: err }))
        )
      }

      if (phoneChanged && memberData.phone) {
        const action = createInviteAction(
          userActionTypePrefix,
          'sms',
          memberData._id,
          memberData.phone,
          memberData.name
        )
        invitePromises.push(
          addUserAction(action)
            .then(res => ({ method: 'sms', success: true, uaId: res._id }))
            .catch(err => ({ method: 'sms', success: false, error: err }))
        )
      }

      if (invitePromises.length > 0) {
        Promise.allSettled(invitePromises).then(results => {
          // Find the original member to preserve existing state
          const originalMember = groupMembers.find(m => m._id === memberData._id)
          let memberToSave = {
            ...originalMember,  // Start with original to preserve existing fields
            ...memberData       // Override with new data
          }

          // Update member with uaId references based on successful invites
          results.forEach(result => {
            if (result.value?.success) {
              if (result.value.method === 'sms') {
                memberToSave.smsUaId = result.value.uaId
                memberToSave.phoneInvitePending = true
                memberToSave.phoneVerified = false
              } else if (result.value.method === 'email') {
                memberToSave.emailUaId = result.value.uaId
                memberToSave.emailInvitePending = true
                memberToSave.emailVerified = false
              }
            }
          })

          const newMembers = groupMembers.map(m =>
            m._id === memberToSave._id ? memberToSave : m
          )
          updateGroupMembers(newMembers)
          doModal(null)
        })
      } else {
        // No contact changes that need invites, just save
        const originalMember = groupMembers.find(m => m._id === memberData._id)
        const memberToSave = { ...originalMember, ...memberData }
        const newMembers = groupMembers.map(m =>
          m._id === memberToSave._id ? memberToSave : m
        )
        updateGroupMembers(newMembers)
        doModal(null)
      }
    } else {
      // Direct save for edits without contact changes
      const originalMember = groupMembers.find(m => m._id === memberData._id)
      const memberToSave = { ...originalMember, ...memberData }
      const newMembers = groupMembers.map(m =>
        m._id === memberToSave._id ? memberToSave : m
      )
      updateGroupMembers(newMembers)
      doModal(null)
    }
  }

  return { handleSaveMember }
}
